import React from 'react';
import PropTypes from 'prop-types';
import EntityContentType from '@/constants/EntityContentType';
import { determineEmailEntityType } from '@/modules/Comms';
import {
  EntityDetailsSummary,
  EntityDetailsSummaryContent,
  useEntityDetails,
} from '@/modules/EntityDetails';
import convertFeedDetailsToEntity from '../lib/convertFeedDetailsToEntity';
import FeedItemPropType from '../lib/FeedItemPropType';

const EMAIL_LOGGED = 'email logged';

function determineCampaignEntityType(markdownContent) {
  // Campaign content has one entity type for multiple types of content.
  // To handle this edge case, parse the content of the markdown to
  // determine which type of content that is being shown.
  if (markdownContent.includes('has a new stretch goal')) {
    return EntityContentType.CAMPAIGN_STRETCH_GOAL_ADDED;
  }
  if (markdownContent.includes('stretch goal has been met')) {
    return EntityContentType.CAMPAIGN_STRETCH_GOAL_MET;
  }
  if (markdownContent.includes('goal has been met')) {
    return EntityContentType.CAMPAIGN_GOAL_MET;
  }
  return EntityContentType.CAMPAIGN_CREATED;
}

function FeedItem({
  id,
  feedItem: {
    action,
    constituentId,
    content,
    contentId,
    contentType,
    feedDetails,
    pinned,
    ...feedItem
  },
}) {
  const entityId = feedDetails?.commsId || contentId;
  const entityType = React.useMemo(() => {
    if (contentType === EntityContentType.CAMPAIGN) {
      return determineCampaignEntityType(content?.[0]?.txt);
    }

    if (action === EMAIL_LOGGED) {
      return determineEmailEntityType(feedDetails?.sender);
    }

    return contentType;
  }, [action, content, contentType, feedDetails]);

  const { openEntity } = useEntityDetails();
  // Normally we'd use a Link rather than a button and click handler, but
  // some feed items have an embedded link, and it is invalid HTML to have an
  // <a> inside of an <a>.
  const handleClick = React.useCallback(() => {
    openEntity({
      constituentId,
      entityType,
      entityId,
    });
  }, [constituentId, content, entityId, entityType]);

  const entity = feedDetails
    ? convertFeedDetailsToEntity(entityType, feedDetails)
    : feedItem;

  return (
    <button
      className="text-left"
      data-testid={`feed-item-${feedItem.feedItemId}`}
      type="button"
      onClick={handleClick}
    >
      <div
        className="w-full rounded-lg border border-grey-300 bg-white p-4"
        id={id}
      >
        <EntityDetailsSummary
          entity={entity}
          entityId={entityId}
          entityType={entityType}
          markdownContent={content}
          pinned={pinned}
        >
          <EntityDetailsSummaryContent
            entity={entity}
            entityType={entityType}
            markdownContent={content}
          />
        </EntityDetailsSummary>
      </div>
    </button>
  );
}

FeedItem.propTypes = {
  id: PropTypes.string,
  feedItem: FeedItemPropType.isRequired,
};

FeedItem.defaultProps = {
  id: '',
};

export default FeedItem;
